import { useEffect, useState, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Checkbox,
  DataTable,
  type DataTableColumn,
  Group,
  ImagePicker,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Title,
  resolveApiOrigin,
} from '@vipso/shared';
import {
  useAdminAddOrgLicenseMutation,
  useAdminUpdateOrgBrandingMutation,
  useAdminUpsertOrgConfigMutation,
  useAdminUpsertOrgSettingMutation,
  useBroadcastOrgRefreshMutation,
  useCreateCustomBillingInvoiceMutation,
  useDeleteSystemNotificationMutation,
  useMarkBillingInvoicePaidMutation,
  useUnlockOrgOperationsMutation,
  useUpdateFeatureSetFeaturesMutation,
  useUpdateOrgBillingSettingsMutation,
  useUpdateTenantConfigMutation,
  useUpsertSystemNotificationMutation,
  useVoidBillingInvoiceMutation,
  type FeatureSetFeatureRow,
  type OrgDetailsPayload,
  type OrgSettingRow,
} from '../api/adminApi';

const APPROVAL_FLOW_TOGGLES: Array<{ code: string; label: string }> = [
  { code: 'APPROVAL_FLOW_CUSTOMER', label: 'Customer approval' },
  { code: 'APPROVAL_FLOW_VENDOR', label: 'Vendor approval' },
  { code: 'APPROVAL_FLOW_PRODUCT', label: 'Product approval' },
  { code: 'APPROVAL_FLOW_WAREHOUSE', label: 'Warehouse approval' },
  { code: 'APPROVAL_FLOW_PROC_SALES', label: 'Sales order approval' },
  { code: 'APPROVAL_FLOW_PROC_PURCHASE', label: 'Purchase order approval' },
];
import { uploadAdminOrgFile } from '../api/uploadAdminOrgFile';
import type { RootState } from '../store/index';

type OrgMember = OrgDetailsPayload['staff'][number];

const MEMBER_COLUMNS: DataTableColumn<OrgMember>[] = [
  { key: 'id', header: 'ID', render: (r) => r.id },
  { key: 'userId', header: 'User ID', render: (r) => r.userId },
  { key: 'name', header: 'Name', render: (r) => r.name },
  { key: 'role', header: 'Role', render: (r) => r.roleName || '—' },
  { key: 'phone', header: 'Phone', render: (r) => r.phone || '—' },
];

const STAFF_COLUMNS: DataTableColumn<OrgMember>[] = [
  ...MEMBER_COLUMNS,
  { key: 'active', header: 'Active', render: (r) => (r.isActive ? 'Yes' : 'No') },
];

type Props = {
  details: OrgDetailsPayload;
  tasksSection?: React.ReactNode;
  rolesSection?: React.ReactNode;
};

export function OrgDetailsPanel({ details, tasksSection, rolesSection }: Props) {
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const [lineDesc, setLineDesc] = useState('Custom service fee');
  const [lineQty, setLineQty] = useState<number>(1);
  const [linePrice, setLinePrice] = useState<number>(0);
  const [dueAt, setDueAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [notifMessage, setNotifMessage] = useState('');
  const [notifType, setNotifType] = useState<string | null>('INFO');
  const [warningDays, setWarningDays] = useState(details.org.warningDays ?? 7);
  const [graceDays, setGraceDays] = useState(details.org.graceDays ?? 3);
  const [licenseType, setLicenseType] = useState<string | null>('extra_seat');
  const [licenseQty, setLicenseQty] = useState<number>(1);
  const [licenseComplimentary, setLicenseComplimentary] = useState(true);
  const [licenseCreateInvoice, setLicenseCreateInvoice] = useState(false);
  const [licenseExtendPeriod, setLicenseExtendPeriod] = useState(false);
  const [primaryColor, setPrimaryColor] = useState(details.org.primaryColor ?? '#cc202e');
  const [logoFileId, setLogoFileId] = useState<string | null>(details.org.logoFileId ?? null);
  const [logoUrl, setLogoUrl] = useState<string | null>(details.org.logoUrl ?? null);
  const [brandMsg, setBrandMsg] = useState<string | null>(null);
  const [brandError, setBrandError] = useState<string | null>(null);
  const [brandUploading, setBrandUploading] = useState(false);

  const [createInvoice, createState] = useCreateCustomBillingInvoiceMutation();
  const [addLicense, addLicenseState] = useAdminAddOrgLicenseMutation();
  const [markPaid, markState] = useMarkBillingInvoicePaidMutation();
  const [voidInvoice, voidState] = useVoidBillingInvoiceMutation();
  const [upsertNotif, upsertNotifState] = useUpsertSystemNotificationMutation();
  const [deleteNotif, deleteNotifState] = useDeleteSystemNotificationMutation();
  const [updateBillingSettings, settingsState] = useUpdateOrgBillingSettingsMutation();
  const [unlockOrg, unlockState] = useUnlockOrgOperationsMutation();
  const [updateFeatureSetFeatures, featureSaveState] = useUpdateFeatureSetFeaturesMutation();
  const [updateOrgBranding, brandingState] = useAdminUpdateOrgBrandingMutation();
  const [upsertOrgSetting, upsertSettingState] = useAdminUpsertOrgSettingMutation();
  const [updateTenantConfig, tenantConfigState] = useUpdateTenantConfigMutation();
  const [upsertOrgConfig, orgConfigState] = useAdminUpsertOrgConfigMutation();
  const [approvalToggleCode, setApprovalToggleCode] = useState<string | null>(null);
  const [savingSettingCode, setSavingSettingCode] = useState<string | null>(null);
  const [broadcastRefresh] = useBroadcastOrgRefreshMutation();

  const [variantForm, setVariantForm] = useState({
    defaultContextLogin: details.tenantConfig?.defaultContextLogin ?? false,
    loginType: details.tenantConfig?.loginType ?? 'OTP',
    maxUsers: details.tenantConfig?.maxUsers ?? 100,
    maxOrgs: details.tenantConfig?.maxOrgs ?? 10,
    enabledModules: details.tenantConfig?.enabledModules ?? [],
  });
  const [orgConfigForm, setOrgConfigForm] = useState({
    defaultContextLogin: details.orgConfig?.defaultContextLogin ?? false,
    loginType: details.orgConfig?.loginType ?? 'OTP',
    maxUsers: details.orgConfig?.maxUsers ?? 100,
    maxOrgs: details.orgConfig?.maxOrgs ?? 10,
    enabledModules: details.orgConfig?.enabledModules ?? [],
  });
  const [configMsg, setConfigMsg] = useState<string | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);

  function approvalSettingEnabled(code: string): boolean {
    const row = details.settings?.find((s) => s.code === code);
    return row?.booleanValue === true;
  }

  async function handleApprovalToggle(code: string, checked: boolean) {
    setApprovalToggleCode(code);
    try {
      await upsertOrgSetting({
        orgId: details.org.id,
        code,
        valueType: 'boolean',
        booleanValue: checked,
      }).unwrap();
      await broadcastRefresh({ orgId: details.org.id });
    } finally {
      setApprovalToggleCode(null);
    }
  }
  const [featureDraft, setFeatureDraft] = useState<
    Record<string, { displayName: string; isEnabled: boolean }>
  >(() => {
    const map: Record<string, { displayName: string; isEnabled: boolean }> = {};
    for (const row of details.featureSetFeatures ?? []) {
      map[row.id] = {
        displayName: row.displayName ?? '',
        isEnabled: row.isEnabled,
      };
    }
    return map;
  });
  const [featureMsg, setFeatureMsg] = useState<string | null>(null);
  const [featureError, setFeatureError] = useState<string | null>(null);

  useEffect(() => {
    const map: Record<string, { displayName: string; isEnabled: boolean }> = {};
    for (const row of details.featureSetFeatures ?? []) {
      map[row.id] = {
        displayName: row.displayName ?? '',
        isEnabled: row.isEnabled,
      };
    }
    setFeatureDraft(map);
  }, [details.featureSetFeatures]);

  useEffect(() => {
    setPrimaryColor(details.org.primaryColor ?? '#cc202e');
    setLogoFileId(details.org.logoFileId ?? null);
    setLogoUrl(details.org.logoUrl ?? null);
  }, [details.org.primaryColor, details.org.logoFileId, details.org.logoUrl]);

  useEffect(() => {
    setVariantForm({
      defaultContextLogin: details.tenantConfig?.defaultContextLogin ?? false,
      loginType: details.tenantConfig?.loginType ?? 'OTP',
      maxUsers: details.tenantConfig?.maxUsers ?? 100,
      maxOrgs: details.tenantConfig?.maxOrgs ?? 10,
      enabledModules: details.tenantConfig?.enabledModules ?? [],
    });
  }, [details.tenantConfig]);

  useEffect(() => {
    setOrgConfigForm({
      defaultContextLogin: details.orgConfig?.defaultContextLogin ?? false,
      loginType: details.orgConfig?.loginType ?? 'OTP',
      maxUsers: details.orgConfig?.maxUsers ?? 100,
      maxOrgs: details.orgConfig?.maxOrgs ?? 10,
      enabledModules: details.orgConfig?.enabledModules ?? [],
    });
  }, [details.orgConfig]);

  async function saveOrgSetting(row: OrgSettingRow, patch: Partial<OrgSettingRow>) {
    setSavingSettingCode(row.code);
    try {
      await upsertOrgSetting({
        orgId: details.org.id,
        code: row.code,
        valueType: patch.valueType ?? row.valueType,
        booleanValue:
          patch.booleanValue !== undefined ? patch.booleanValue : row.booleanValue,
        stringValue: patch.stringValue !== undefined ? patch.stringValue : row.stringValue,
        numberValue: patch.numberValue !== undefined ? patch.numberValue : row.numberValue,
        arrayValue: patch.arrayValue !== undefined ? patch.arrayValue : row.arrayValue,
        enumOptions: patch.enumOptions !== undefined ? patch.enumOptions : row.enumOptions,
      }).unwrap();
      await broadcastRefresh({ orgId: details.org.id });
    } finally {
      setSavingSettingCode(null);
    }
  }

  const pendingInvoices = (details.invoices ?? []).filter((i) => i.status === 'pending');

  const overview = (
    <Stack gap="md">
      <Group gap="sm">
        <Text size="sm" c="dimmed">
          ID: {details.org.id} · Unique ID: {details.org.uniqueId || '—'} · Status:{' '}
          {details.org.status} · Tenant: {details.org.tenantId}
        </Text>
        {details.org.operationsLocked ? (
          <Badge color="red">Operations locked</Badge>
        ) : (
          <Badge color="green">Writable</Badge>
        )}
      </Group>
      {details.org.paymentDueAt ? (
        <Text size="sm">
          Payment due: {new Date(details.org.paymentDueAt).toLocaleDateString()}
        </Text>
      ) : null}
      {details.org.operationsLocked ? (
        <Alert color="red" title="Locked">
          {details.org.lockReason === 'UNPAID_INVOICE'
            ? 'Locked due to unpaid invoice. Mark invoice paid or unlock manually.'
            : details.org.lockReason || 'Operations are locked.'}
          <Group mt="sm">
            <Button
              size="xs"
              loading={unlockState.isLoading}
              onClick={() => void unlockOrg({ orgId: details.org.id })}
            >
              Unlock operations
            </Button>
          </Group>
        </Alert>
      ) : null}
      <Text size="sm">Phone: {details.org.phone || '—'}</Text>
      <Text size="sm">
        Subscription:{' '}
        {details.subscription
          ? `${details.subscription.status} (${details.subscription.billingPeriod})`
          : 'None'}
      </Text>
      <Text size="sm">Pending invoices: {pendingInvoices.length}</Text>
      {tasksSection}
      {rolesSection}
    </Stack>
  );

  const staffTable = (
    <DataTable
      columns={STAFF_COLUMNS}
      rows={details.staff ?? []}
      getRowId={(r) => r.id}
      emptyTitle="No staff"
    />
  );

  const ownersTable = (
    <DataTable
      columns={MEMBER_COLUMNS}
      rows={details.owners ?? []}
      getRowId={(r) => r.id}
      emptyTitle="No owners"
    />
  );

  const features = (
    <Stack gap="sm">
      <Title order={6}>Feature set (labels + entitlements)</Title>
      <Text size="sm" c="dimmed">
        Set displayName on module primary keys (e.g. customer.view → Farmers) to rename nav/titles
        for this org. Leave blank to use the app default i18n label.
      </Text>
      {featureMsg ? <Alert color="green" title={featureMsg} /> : null}
      {featureError ? <Alert color="red" title={featureError} /> : null}
      {(details.featureSetFeatures ?? []).length === 0 ? (
        <Text size="sm" c="dimmed">
          No feature set assigned to this org yet. Assign one from Features.
        </Text>
      ) : (
        <>
          <DataTable
            columns={[
              {
                key: 'key',
                header: 'Feature key',
                render: (r: FeatureSetFeatureRow) => r.featureKey,
              },
              {
                key: 'catalog',
                header: 'Catalog name',
                render: (r: FeatureSetFeatureRow) => r.name || '—',
              },
              {
                key: 'module',
                header: 'Module',
                render: (r: FeatureSetFeatureRow) => r.module || '—',
              },
              {
                key: 'displayName',
                header: 'Display name override',
                render: (r: FeatureSetFeatureRow) => (
                  <TextInput
                    size="xs"
                    placeholder="e.g. Farmers"
                    value={featureDraft[r.id]?.displayName ?? r.displayName ?? ''}
                    onChange={(e) => {
                      const displayName = e.currentTarget.value;
                      setFeatureDraft((prev) => ({
                        ...prev,
                        [r.id]: {
                          displayName,
                          isEnabled: prev[r.id]?.isEnabled ?? r.isEnabled,
                        },
                      }));
                    }}
                  />
                ),
              },
              {
                key: 'enabled',
                header: 'Enabled',
                render: (r: FeatureSetFeatureRow) => (
                  <Checkbox
                    checked={featureDraft[r.id]?.isEnabled ?? r.isEnabled}
                    onChange={(e) => {
                      const isEnabled = e.currentTarget.checked;
                      setFeatureDraft((prev) => ({
                        ...prev,
                        [r.id]: {
                          displayName: prev[r.id]?.displayName ?? r.displayName ?? '',
                          isEnabled,
                        },
                      }));
                    }}
                  />
                ),
              },
            ]}
            rows={details.featureSetFeatures ?? []}
            getRowId={(r) => r.id}
          />
          <Group justify="flex-end">
            <Button
              loading={featureSaveState.isLoading}
              onClick={() => {
                void (async () => {
                  setFeatureMsg(null);
                  setFeatureError(null);
                  try {
                    const rows = details.featureSetFeatures ?? [];
                    const featureSetId = rows[0]?.featureSetId;
                    if (!featureSetId) {
                      setFeatureError('Missing feature set id.');
                      return;
                    }
                    const items = rows.map((row) => {
                      const draft = featureDraft[row.id] ?? {
                        displayName: row.displayName ?? '',
                        isEnabled: row.isEnabled,
                      };
                      return {
                        id: row.id,
                        displayName: draft.displayName.trim() || null,
                        isEnabled: draft.isEnabled,
                      };
                    });
                    await updateFeatureSetFeatures({ featureSetId, items }).unwrap();
                    await broadcastRefresh({ orgId: details.org.id }).unwrap();
                    setFeatureMsg('Feature labels saved. Org clients will refresh.');
                  } catch {
                    setFeatureError('Failed to save feature set.');
                  }
                })();
              }}
            >
              Save feature labels
            </Button>
          </Group>
        </>
      )}
      <Title order={6}>Effective feature keys</Title>
      <Group gap={6}>
        {(details.featureKeys ?? []).length === 0 ? (
          <Text size="sm" c="dimmed">
            None
          </Text>
        ) : (
          details.featureKeys.map((k) => (
            <Badge key={k} variant="light">
              {k}
            </Badge>
          ))
        )}
      </Group>
      <Title order={6}>Enabled modules</Title>
      <Group gap={6}>
        {(details.enabledModules ?? []).length === 0 ? (
          <Text size="sm" c="dimmed">
            None
          </Text>
        ) : (
          details.enabledModules.map((k) => (
            <Badge key={k} color="blue" variant="light">
              {k}
            </Badge>
          ))
        )}
      </Group>
    </Stack>
  );

  const invoices = (
    <Stack gap="lg">
      <Stack gap="sm">
        <Title order={6}>Current subscription</Title>
        {details.subscription ? (
          <Group gap="sm">
            <Badge variant="light">{details.subscription.status}</Badge>
            <Text size="sm">
              Period: {details.subscription.billingPeriod} · Seats:{' '}
              {details.subscription.seatCountOverride ?? '—'}
            </Text>
            <Text size="sm" c="dimmed">
              {details.subscription.currentPeriodStart
                ? new Date(details.subscription.currentPeriodStart).toLocaleDateString()
                : '—'}{' '}
              →{' '}
              {details.subscription.currentPeriodEnd
                ? new Date(details.subscription.currentPeriodEnd).toLocaleDateString()
                : '—'}
            </Text>
          </Group>
        ) : (
          <Text size="sm" c="dimmed">
            No active subscription
          </Text>
        )}
      </Stack>

      <Stack gap="sm">
        <Title order={6}>Add license / add-on</Title>
        <Select
          label="Type"
          value={licenseType}
          onChange={setLicenseType}
          data={[
            { value: 'extra_seat', label: 'Extra user seat(s)' },
            { value: 'entity.approvals', label: 'Entity Approvals' },
          ]}
          allowDeselect={false}
        />
        {licenseType === 'extra_seat' ? (
          <NumberInput
            label="Quantity"
            value={licenseQty}
            onChange={(v) => setLicenseQty(typeof v === 'number' ? v : 1)}
            min={1}
            max={10}
          />
        ) : null}
        <Checkbox
          label="Complimentary (activate immediately, no charge)"
          checked={licenseComplimentary}
          onChange={(e) => setLicenseComplimentary(e.currentTarget.checked)}
        />
        <Checkbox
          label="Also create invoice"
          checked={licenseCreateInvoice}
          onChange={(e) => setLicenseCreateInvoice(e.currentTarget.checked)}
        />
        <Checkbox
          label="Extend period by 1 year"
          checked={licenseExtendPeriod}
          onChange={(e) => setLicenseExtendPeriod(e.currentTarget.checked)}
        />
        <Button
          loading={addLicenseState.isLoading}
          disabled={!licenseType}
          onClick={() =>
            void addLicense({
              orgId: details.org.id,
              type: licenseType as 'extra_seat' | 'entity.approvals',
              qty: licenseType === 'extra_seat' ? licenseQty : 1,
              complimentary: licenseComplimentary,
              createInvoice: licenseCreateInvoice || !licenseComplimentary,
              extendPeriod: licenseExtendPeriod,
              billingPeriod: 'yearly',
            })
          }
        >
          Add license
        </Button>
        {addLicenseState.isError ? (
          <Alert color="red" title="Failed">
            {(addLicenseState.error as { data?: { message?: string } })?.data?.message ||
              'Could not add license'}
          </Alert>
        ) : null}
      </Stack>

      <Stack gap="sm">
        <Title order={6}>Create custom invoice</Title>
        <TextInput
          label="Description"
          value={lineDesc}
          onChange={(e) => setLineDesc(e.currentTarget.value)}
        />
        <Group grow>
          <NumberInput
            label="Quantity"
            value={lineQty}
            onChange={(v) => setLineQty(typeof v === 'number' ? v : 1)}
            min={1}
          />
          <NumberInput
            label="Unit price (excl. GST)"
            value={linePrice}
            onChange={(v) => setLinePrice(typeof v === 'number' ? v : 0)}
            min={0}
          />
        </Group>
        <TextInput
          type="date"
          label="Due date"
          value={dueAt}
          onChange={(e) => setDueAt(e.currentTarget.value)}
          description="Payment deadline shown on the invoice and used for the tenant payment banner"
          required
        />
        <Textarea
          label="Notes"
          value={invoiceNotes}
          onChange={(e) => setInvoiceNotes(e.currentTarget.value)}
        />
        <Button
          loading={createState.isLoading}
          disabled={!lineDesc.trim() || linePrice <= 0 || !dueAt}
          onClick={() =>
            void (async () => {
              await createInvoice({
                orgId: details.org.id,
                lines: [{ description: lineDesc, quantity: lineQty, unitPrice: linePrice }],
                // Date-only → end of day so banner + lock schedule treat "today" as still due.
                dueAt: dueAt ? `${dueAt}T23:59:59.999Z` : null,
                notes: invoiceNotes || null,
              }).unwrap();
              setLinePrice(0);
              setInvoiceNotes('');
              // Domain patches the payment banner via org-data-changed — no force-refresh
              // (that remounted the tenant Settings page and caused double flicker).
            })()
          }
        >
          Generate invoice (+18% GST)
        </Button>
      </Stack>
      <Title order={6}>Invoices</Title>
      <DataTable
        columns={[
          { key: 'id', header: 'ID', render: (r) => r.id },
          { key: 'number', header: 'Number', render: (r) => r.invoiceNumber },
          { key: 'status', header: 'Status', render: (r) => r.status },
          {
            key: 'total',
            header: 'Grand total',
            render: (r) =>
              `${r.currency} ${r.grandTotal ?? r.total}${
                r.taxTotal ? ` (GST ₹${r.taxTotal})` : ''
              }`,
          },
          {
            key: 'due',
            header: 'Due',
            render: (r) => (r.dueAt ? new Date(r.dueAt).toLocaleDateString() : '—'),
          },
          {
            key: 'actions',
            header: '',
            render: (r) => (
              <Group gap={4}>
                {r.pdfFileId ? (
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => {
                      const origin = resolveApiOrigin();
                      if (!origin) return;
                      window.open(`${origin}/api/files/${r.pdfFileId}`, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    Download invoice
                  </Button>
                ) : null}
                {r.status === 'pending' ? (
                  <>
                    <Button
                      size="xs"
                      loading={markState.isLoading}
                      onClick={() => void markPaid({ invoiceId: r.id })}
                    >
                      Mark paid
                    </Button>
                    <Button
                      size="xs"
                      variant="default"
                      loading={voidState.isLoading}
                      onClick={() => void voidInvoice({ invoiceId: r.id })}
                    >
                      Void
                    </Button>
                  </>
                ) : (
                  <Text size="xs" c="dimmed">
                    {r.paidAt
                      ? `Paid ${(() => {
                          const d = new Date(r.paidAt);
                          return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
                        })()}`
                      : '—'}
                  </Text>
                )}
              </Group>
            ),
          },
        ]}
        rows={details.invoices ?? []}
        getRowId={(r) => r.id}
        emptyTitle="No invoices"
      />
    </Stack>
  );

  const notifications = (
    <Stack gap="lg">
      <Stack gap="sm">
        <Title order={6}>Create notification</Title>
        <Textarea
          label="Message"
          value={notifMessage}
          onChange={(e) => setNotifMessage(e.currentTarget.value)}
        />
        <Select
          label="Type"
          value={notifType}
          onChange={setNotifType}
          data={[
            { value: 'INFO', label: 'Info' },
            { value: 'WARNING', label: 'Warning' },
            { value: 'CRITICAL', label: 'Critical' },
            { value: 'MAINTENANCE', label: 'Maintenance' },
          ]}
          allowDeselect={false}
        />
        <Button
          loading={upsertNotifState.isLoading}
          disabled={!notifMessage.trim() || !notifType}
          onClick={() =>
            void upsertNotif({
              message: notifMessage,
              type: notifType,
              orgId: details.org.id,
              priority:
                notifType === 'CRITICAL' ? 100 : notifType === 'WARNING' ? 50 : 0,
              source: 'manual',
              isActive: true,
            })
          }
        >
          Publish
        </Button>
      </Stack>
      <DataTable
        columns={[
          { key: 'id', header: 'ID', render: (r) => r.id },
          { key: 'type', header: 'Type', render: (r) => r.type },
          { key: 'message', header: 'Message', render: (r) => r.message },
          { key: 'active', header: 'Active', render: (r) => (r.isActive ? 'Yes' : 'No') },
          { key: 'source', header: 'Source', render: (r) => r.source },
          {
            key: 'actions',
            header: '',
            render: (r) => (
              <Button
                size="xs"
                variant="default"
                loading={deleteNotifState.isLoading}
                onClick={() => void deleteNotif({ id: r.id })}
              >
                Remove
              </Button>
            ),
          },
        ]}
        rows={details.notifications ?? []}
        getRowId={(r) => r.id}
        emptyTitle="No notifications"
      />
    </Stack>
  );

  const settings = (
    <Stack gap="md">
      <Title order={6}>Payment lock settings</Title>
      <Group grow>
        <NumberInput
          label="Warning days before due"
          value={warningDays}
          onChange={(v) => setWarningDays(typeof v === 'number' ? v : 7)}
          min={0}
        />
        <NumberInput
          label="Grace days after due"
          value={graceDays}
          onChange={(v) => setGraceDays(typeof v === 'number' ? v : 3)}
          min={0}
        />
      </Group>
      <Button
        loading={settingsState.isLoading}
        onClick={() =>
          void updateBillingSettings({
            orgId: details.org.id,
            warningDays,
            graceDays,
          })
        }
      >
        Save billing settings
      </Button>
      <Title order={6}>Approval workflows</Title>
      <Text size="sm" c="dimmed">
        When off (default), creates skip approval and go straight to Approved. Status
        columns stay hidden in the tenant app for disabled modules.
      </Text>
      <Stack gap="sm">
        {APPROVAL_FLOW_TOGGLES.map((item) => (
          <Group key={item.code} justify="space-between" wrap="nowrap">
            <Text size="sm">{item.label}</Text>
            <Switch
              checked={approvalSettingEnabled(item.code)}
              disabled={
                upsertSettingState.isLoading && approvalToggleCode === item.code
              }
              onChange={(e) =>
                void handleApprovalToggle(item.code, e.currentTarget.checked)
              }
              aria-label={item.label}
            />
          </Group>
        ))}
      </Stack>
      <Title order={6}>Org settings</Title>
      <Text size="sm" c="dimmed">
        Admin-only. Only existing settings can be changed (typed by valueType).
      </Text>
      {(details.settings ?? []).length === 0 ? (
        <Text size="sm" c="dimmed">
          No org settings
        </Text>
      ) : (
        <Stack gap="md">
          {(details.settings ?? []).map((row) => {
            const busy = savingSettingCode === row.code && upsertSettingState.isLoading;
            return (
              <Group key={row.id} align="flex-end" wrap="nowrap" gap="md">
                <Stack gap={2} style={{ minWidth: 180 }}>
                  <Text size="sm" fw={600}>
                    {row.code}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {row.valueType}
                  </Text>
                </Stack>
                {row.valueType === 'boolean' ? (
                  <Switch
                    checked={row.booleanValue === true}
                    disabled={busy}
                    onChange={(e) =>
                      void saveOrgSetting(row, { booleanValue: e.currentTarget.checked })
                    }
                    aria-label={row.code}
                  />
                ) : null}
                {row.valueType === 'number' ? (
                  <NumberInput
                    style={{ flex: 1 }}
                    value={row.numberValue ?? 0}
                    disabled={busy}
                    onChange={(v) =>
                      void saveOrgSetting(row, {
                        numberValue: typeof v === 'number' ? v : Number(v) || 0,
                      })
                    }
                  />
                ) : null}
                {row.valueType === 'string' ? (
                  <TextInput
                    style={{ flex: 1 }}
                    value={row.stringValue ?? ''}
                    disabled={busy}
                    onBlur={(e) =>
                      void saveOrgSetting(row, { stringValue: e.currentTarget.value })
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        void saveOrgSetting(row, {
                          stringValue: (e.target as HTMLInputElement).value,
                        });
                      }
                    }}
                  />
                ) : null}
                {row.valueType === 'enum' ? (
                  <Select
                    style={{ flex: 1 }}
                    value={row.stringValue ?? null}
                    data={(row.enumOptions ?? []).map((o) => ({ value: o, label: o }))}
                    disabled={busy}
                    onChange={(v) => void saveOrgSetting(row, { stringValue: v })}
                    allowDeselect={false}
                  />
                ) : null}
                {row.valueType === 'array' ? (
                  <MultiSelect
                    style={{ flex: 1 }}
                    data={[
                      ...new Set([
                        ...(row.enumOptions ?? []),
                        ...((row.arrayValue as string[] | null) ?? []).map(String),
                      ]),
                    ].map((o) => ({ value: o, label: o }))}
                    value={((row.arrayValue as string[] | null) ?? []).map(String)}
                    searchable
                    disabled={busy}
                    onChange={(v) => void saveOrgSetting(row, { arrayValue: v })}
                  />
                ) : null}
              </Group>
            );
          })}
        </Stack>
      )}
    </Stack>
  );

  const variantConfig = (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Tenant (variant) config for tenant #{details.org.tenantId}. Shared by all orgs on this
        tenant. New orgs copy these values into Org config.
      </Text>
      {configMsg ? <Alert color="green" title={configMsg} /> : null}
      {configError ? <Alert color="red" title={configError} /> : null}
      <Switch
        label="Default context login (skip org picker)"
        checked={variantForm.defaultContextLogin}
        onChange={(e) =>
          setVariantForm((f) => ({ ...f, defaultContextLogin: e.currentTarget.checked }))
        }
      />
      <Select
        label="Login type"
        value={variantForm.loginType}
        data={[
          { value: 'OTP', label: 'OTP' },
          { value: 'PASSWORD', label: 'Password' },
        ]}
        onChange={(v) => setVariantForm((f) => ({ ...f, loginType: v || 'OTP' }))}
        allowDeselect={false}
      />
      <Group grow>
        <NumberInput
          label="Max users"
          value={variantForm.maxUsers}
          min={1}
          onChange={(v) =>
            setVariantForm((f) => ({ ...f, maxUsers: typeof v === 'number' ? v : 100 }))
          }
        />
        <NumberInput
          label="Max orgs"
          value={variantForm.maxOrgs}
          min={1}
          onChange={(v) =>
            setVariantForm((f) => ({ ...f, maxOrgs: typeof v === 'number' ? v : 10 }))
          }
        />
      </Group>
      <TextInput
        label="Enabled modules (comma-separated)"
        value={variantForm.enabledModules.join(', ')}
        onChange={(e) =>
          setVariantForm((f) => ({
            ...f,
            enabledModules: e.currentTarget.value
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean),
          }))
        }
      />
      <Button
        loading={tenantConfigState.isLoading}
        onClick={() => {
          setConfigMsg(null);
          setConfigError(null);
          void updateTenantConfig({
            tenantId: String(details.org.tenantId),
            defaultContextLogin: variantForm.defaultContextLogin,
            loginType: variantForm.loginType,
            maxUsers: variantForm.maxUsers,
            maxOrgs: variantForm.maxOrgs,
            enabledModules: variantForm.enabledModules,
          })
            .unwrap()
            .then(() => setConfigMsg('Variant config saved'))
            .catch((err: unknown) =>
              setConfigError(err instanceof Error ? err.message : 'Failed to save variant config'),
            );
        }}
      >
        Save variant config
      </Button>
    </Stack>
  );

  const orgConfigSection = (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Per-org overrides. Defaults were copied from the tenant (variant) config when the org was
        created. Changes here affect only this organization.
      </Text>
      <Switch
        label="Default context login (skip org picker)"
        checked={orgConfigForm.defaultContextLogin}
        onChange={(e) =>
          setOrgConfigForm((f) => ({ ...f, defaultContextLogin: e.currentTarget.checked }))
        }
      />
      <Select
        label="Login type"
        value={orgConfigForm.loginType}
        data={[
          { value: 'OTP', label: 'OTP' },
          { value: 'PASSWORD', label: 'Password' },
        ]}
        onChange={(v) => setOrgConfigForm((f) => ({ ...f, loginType: v || 'OTP' }))}
        allowDeselect={false}
      />
      <Group grow>
        <NumberInput
          label="Max users"
          value={orgConfigForm.maxUsers}
          min={1}
          onChange={(v) =>
            setOrgConfigForm((f) => ({ ...f, maxUsers: typeof v === 'number' ? v : 100 }))
          }
        />
        <NumberInput
          label="Max orgs"
          value={orgConfigForm.maxOrgs}
          min={1}
          onChange={(v) =>
            setOrgConfigForm((f) => ({ ...f, maxOrgs: typeof v === 'number' ? v : 10 }))
          }
        />
      </Group>
      <TextInput
        label="Enabled modules (comma-separated)"
        value={orgConfigForm.enabledModules.join(', ')}
        onChange={(e) =>
          setOrgConfigForm((f) => ({
            ...f,
            enabledModules: e.currentTarget.value
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean),
          }))
        }
      />
      <Button
        loading={orgConfigState.isLoading}
        onClick={() => {
          setConfigMsg(null);
          setConfigError(null);
          void upsertOrgConfig({
            orgId: details.org.id,
            defaultContextLogin: orgConfigForm.defaultContextLogin,
            loginType: orgConfigForm.loginType,
            maxUsers: orgConfigForm.maxUsers,
            maxOrgs: orgConfigForm.maxOrgs,
            enabledModules: orgConfigForm.enabledModules,
          })
            .unwrap()
            .then(() => setConfigMsg('Org config saved'))
            .catch((err: unknown) =>
              setConfigError(err instanceof Error ? err.message : 'Failed to save org config'),
            );
        }}
      >
        Save org config
      </Button>
    </Stack>
  );

  const metrics = (
    <DataTable
      columns={[
        { key: 'label', header: 'Metric', render: (r) => r.label },
        { key: 'used', header: 'Used', render: (r) => r.used },
        {
          key: 'entitled',
          header: 'Entitled / limit',
          render: (r) => (r.entitled != null ? r.entitled : '—'),
        },
      ]}
      rows={
        details.metrics ??
        details.moduleCounts.map((m) => ({
          key: m.module,
          label: m.module,
          used: m.count,
          entitled: null,
        }))
      }
      getRowId={(r) => r.key}
      emptyTitle="No metrics"
    />
  );

  const branding = (
    <Stack gap="md">
      <Title order={6}>Org branding</Title>
      <Text size="sm" c="dimmed">
        Primary color drives the tenant app theme. Logo is uploaded to the API and streamed to
        clients via `/api/files/:id` (no direct public image URLs).
      </Text>
      {brandMsg ? <Alert color="green" title={brandMsg} /> : null}
      {brandError ? <Alert color="red" title={brandError} /> : null}
      <Group align="flex-end" gap="md">
        <TextInput
          label="Primary color"
          description="#RRGGBB"
          value={primaryColor}
          onChange={(e) => setPrimaryColor(e.currentTarget.value)}
          style={{ flex: 1, maxWidth: 220 }}
        />
        <input
          type="color"
          aria-label="Pick primary color"
          value={/^#[0-9A-Fa-f]{6}$/.test(primaryColor) ? primaryColor : '#cc202e'}
          onChange={(e) => setPrimaryColor(e.currentTarget.value)}
          style={{ width: 48, height: 36, border: 'none', background: 'transparent', cursor: 'pointer' }}
        />
        <Button
          loading={brandingState.isLoading}
          onClick={() => {
            setBrandMsg(null);
            setBrandError(null);
            void updateOrgBranding({
              orgId: details.org.id,
              primaryColor: primaryColor.trim() || null,
            })
              .unwrap()
              .then((res) => {
                setPrimaryColor(res.adminUpdateOrgBranding.primaryColor ?? primaryColor);
                setBrandMsg('Primary color saved');
                void broadcastRefresh({ orgId: details.org.id });
              })
              .catch((err: unknown) => {
                setBrandError(err instanceof Error ? err.message : 'Failed to save color');
              });
          }}
        >
          Save color
        </Button>
      </Group>
      <ImagePicker
        label="Org logo"
        value={logoUrl}
        shape="square"
        clearable
        disabled={brandUploading || brandingState.isLoading || !accessToken}
        onUpload={async (file) => {
          if (!accessToken) {
            setBrandError('Not signed in');
            return;
          }
          setBrandMsg(null);
          setBrandError(null);
          setBrandUploading(true);
          try {
            const uploaded = await uploadAdminOrgFile(details.org.id, file, accessToken);
            const res = await updateOrgBranding({
              orgId: details.org.id,
              logoFileId: uploaded.id,
            }).unwrap();
            setLogoFileId(res.adminUpdateOrgBranding.logoFileId ?? uploaded.id);
            setLogoUrl(res.adminUpdateOrgBranding.logoUrl ?? uploaded.url);
            setBrandMsg('Logo updated');
            void broadcastRefresh({ orgId: details.org.id });
          } catch (err) {
            setBrandError(err instanceof Error ? err.message : 'Logo upload failed');
          } finally {
            setBrandUploading(false);
          }
        }}
        onClear={async () => {
          setBrandMsg(null);
          setBrandError(null);
          try {
            await updateOrgBranding({
              orgId: details.org.id,
              logoFileId: null,
            }).unwrap();
            setLogoFileId(null);
            setLogoUrl(null);
            setBrandMsg('Logo cleared');
            void broadcastRefresh({ orgId: details.org.id });
          } catch (err) {
            setBrandError(err instanceof Error ? err.message : 'Failed to clear logo');
          }
        }}
      />
      {logoFileId ? (
        <Text size="xs" c="dimmed">
          Stream file id: {logoFileId}
        </Text>
      ) : null}
    </Stack>
  );

  const sections: Array<{ value: string; title: string; content: ReactNode }> = [
    { value: 'overview', title: 'Overview', content: overview },
    { value: 'branding', title: 'Branding', content: branding },
    { value: 'staff', title: 'Staff', content: staffTable },
    { value: 'owners', title: 'Owners', content: ownersTable },
    { value: 'features', title: 'Features', content: features },
    { value: 'billing', title: 'Billing', content: invoices },
    { value: 'notifications', title: 'Notifications', content: notifications },
    { value: 'settings', title: 'Org settings', content: settings },
    { value: 'variant', title: 'Variant config', content: variantConfig },
    { value: 'orgConfig', title: 'Org config', content: orgConfigSection },
    { value: 'metrics', title: 'Metrics', content: metrics },
  ];

  return (
    <Accordion multiple defaultValue={['overview', 'billing', 'settings']}>
      {sections.map((section) => (
        <Accordion.Item key={section.value} value={section.value}>
          <Accordion.Control>{section.title}</Accordion.Control>
          <Accordion.Panel>{section.content}</Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion>
  );
}
